// ts-rest contract for the Desk Vulnerability Assessment.
//
// The 15-question Yes/No vulnerability screening an EA / IM completes (typically
// at client onboarding). The endpoints: a `get` (the client's current assessment
// or an empty blank), `save` (upsert draft), and `complete` (draft → completed).
//
// Only the raw Yes/No answers + the two commentary boxes are stored; the score
// and tier are computed by the shared `computeVulnerabilityAssessment` helper
// (the web form recomputes the same live), so the `derived` block is a mirror,
// not the source of truth.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionErrorSchema } from './agent';

const c = initContract();

const errorBody = z.object({ error: z.string() });

const statusSchema = z.enum(['draft', 'completed']);
const tierSchema = z.enum(['low', 'moderate', 'high']);

// The raw Yes/No answers, keyed by question key (only answered keys present).
export const vulnerabilityAnswersSchema = z.record(z.string(), z.boolean());
export type VulnerabilityAnswersDto = z.infer<typeof vulnerabilityAnswersSchema>;

// Controller-derived figures (mirror of computeVulnerabilityAssessment).
export const vulnerabilityAssessmentDerivedSchema = z.object({
    score: z.number(),
    answered: z.number(),
    tier: tierSchema,
    tierLabel: z.string(),
});

export const vulnerabilityAssessmentDtoSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    imUserId: z.string(),
    status: statusSchema,
    answers: vulnerabilityAnswersSchema,
    imCommentary: z.string().nullable(),
    complianceCommentary: z.string().nullable(),
    derived: vulnerabilityAssessmentDerivedSchema,
    createdAt: z.string(),
    completedAt: z.string().nullable(),
});
export type VulnerabilityAssessmentDto = z.infer<typeof vulnerabilityAssessmentDtoSchema>;

// GET response: the current assessment (`exists: true`) or null. `clientName`
// is always present for the page header.
export const vulnerabilityAssessmentGetResponseSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    exists: z.boolean(),
    assessment: vulnerabilityAssessmentDtoSchema.nullable(),
});
export type VulnerabilityAssessmentGetResponse = z.infer<
    typeof vulnerabilityAssessmentGetResponseSchema
>;

export const vulnerabilityAssessmentContract = c.router(
    {
        getVulnerabilityAssessment: {
            method: 'GET',
            path: '/desk/vulnerability/assessment/:clientId',
            pathParams: z.object({ clientId: z.string() }),
            responses: {
                200: vulnerabilityAssessmentGetResponseSchema,
                403: errorBody,
                404: errorBody,
            },
            summary: "A client's Vulnerability Assessment (or a blank)",
        },
        saveVulnerabilityAssessment: {
            method: 'POST',
            path: '/desk/vulnerability/assessment/save',
            body: z.object({
                id: z.string().optional(),
                clientId: z.string().min(1),
                answers: vulnerabilityAnswersSchema,
                imCommentary: z.string().nullable().optional(),
                complianceCommentary: z.string().nullable().optional(),
            }),
            responses: {
                200: z.object({ assessment: vulnerabilityAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Create or patch the working draft of a vulnerability assessment',
        },
        completeVulnerabilityAssessment: {
            method: 'POST',
            path: '/desk/vulnerability/assessment/:id/complete',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: z.object({ assessment: vulnerabilityAssessmentDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Sign off a vulnerability assessment (draft → completed)',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
